import { useEffect, useState } from 'react';
import { MdError } from 'react-icons/md';
import { Button, Modal } from 'rsuite';
import { CampaignType } from '../../lib/schema';
import styles from './CampaignTypeUpsertModal.module.scss';

type Props = {
  open: boolean;
  initialValue?: CampaignType | null;
  onClose: () => void;
  onSave: (value: Omit<CampaignType, 'order'>) => void;
  handleValidateName: (name: string, typeId: string | null) => boolean;
};

export const CampaignTypeUpsertModal = ({ open, onClose, initialValue = null, onSave, handleValidateName }: Props) => {
  const isEdit = Boolean(initialValue?.typeId);
  const [name, setName] = useState('');
  const [isNameValid, setIsNameValid] = useState(true);

  useEffect(() => {
    if (open) {
      setName(initialValue?.campaignType ?? '');
    }
  }, [open, initialValue]);

  const handleSave = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;

    onSave({
      typeId: initialValue?.typeId ?? null,
      campaignType: trimmed,
    });
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isNameValid = !handleValidateName(e.target.value, initialValue?.typeId || null);
    setIsNameValid(isNameValid);
    setName(e.target.value);
  };

  const handleClose = () => {
    setName('');
    setIsNameValid(true);
    onClose();
  };

  return (
    <Modal show={open} onHide={handleClose} size="sm" className={styles.modal}>
      <form onSubmit={handleSave}>
        <Modal.Header>
          <h1 className={styles.title}>{isEdit ? 'Editar tipo de campanha' : 'Novo tipo de campanha'}</h1>
        </Modal.Header>

        <Modal.Body>
          <p className={styles.subtitle}>Crie diferentes tipos de campanhas promocionais e defina a priorização entre eles.</p>

          <label className={styles.label} htmlFor="campaignTypeName">
            Nome do tipo de campanha
          </label>

          <div className={styles.inputContainer} data-is-valid={isNameValid}>
            <input id="campaignTypeName" type="text" className={styles.input} placeholder="Defina um nome" value={name} onChange={handleNameChange} autoFocus />

            {!isNameValid ? (
              <span className={styles.errorIcon}>
                <MdError size={20} />
              </span>
            ) : null}
          </div>

          {!isNameValid ? (
            <div className={styles.error}>
              <span>
                <MdError size={20} />
              </span>
              <p>
                Não é possível salvar, já existe uma campanha com esse nome. <br /> <span className={styles.highlight}>Altere o nome e tente novamente.</span>
              </p>
            </div>
          ) : null}
        </Modal.Body>

        <Modal.Footer className={styles.footer}>
          <div className={styles.divider} />
          <Button type="button" appearance="ghost" onClick={handleClose} disabled={!isNameValid} className={styles.btnGhost}>
            Cancelar
          </Button>

          <Button type="submit" appearance="primary" disabled={name.trim().length === 0 || !isNameValid} className={styles.btnPrimary} onClick={handleSave}>
            Salvar
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
